// src/pages/AdminPage.jsx
import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase'; // Use your config path
import { useAuth } from '../context/AuthContext'; // Import useAuth hook
import { useRentmanData } from '../hooks/useRentmanData';
import { toast } from 'sonner';

// Shadcn UI Imports
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Loader2, MoreHorizontal, Pencil, Trash2, UserX } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const baseUrl = import.meta.env.DEV 
          ? 'http://localhost:5001/dagslys-dc425/europe-west1'
          : 'https://europe-west1-dagslys-dc425.cloudfunctions.net';

function AdminPage() {
  const { currentUser } = useAuth(); // Use useAuth hook to get currentUser
  const { refreshData, clearCache } = useRentmanData();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingUser, setProcessingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    notificationEmails: [],
    useDefaultAdmins: true,
  });
  const [newEmail, setNewEmail] = useState("");
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [resettingCache, setResettingCache] = useState(false);
  const [cacheTTL, setCacheTTL] = useState(14); // Default to 14 days
  const [updatingTTL, setUpdatingTTL] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    console.log("AdminPage: Setting up Firestore listener for users collection...");

    // Query users collection, order by creation time
    const usersQuery = query(collection(db, "users"), orderBy("createdAt", "desc"));

    // Use onSnapshot for real-time updates
    const unsubscribe = onSnapshot(usersQuery, (querySnapshot) => {
      const usersData = [];
      querySnapshot.forEach((doc) => {
        usersData.push({ id: doc.id, ...doc.data() }); // Include document ID (which is the UID)
      });
      console.log(`AdminPage: Received ${usersData.length} user(s)`);
      setUsers(usersData);
      setLoading(false);
    }, (err) => { // Handle errors from the listener
      console.error("AdminPage: Error fetching users:", err);
      setError("Failed to load user data. Please check permissions or try again.");
      setLoading(false);
    });

    // Cleanup function to unsubscribe when component unmounts
    return () => {
      console.log("AdminPage: Unsubscribing from Firestore listener.");
      unsubscribe();
    };
  }, [currentUser]); // Add currentUser as a dependency

  // Add new useEffect for notification settings
  useEffect(() => {
    const fetchNotificationSettings = async () => {
      if (!currentUser) {
        console.log("No current user, skipping notification settings fetch");
        setLoadingSettings(false);
        return;
      }
      
      try {
        console.log("Fetching notification settings...");
        const idToken = await currentUser.getIdToken();
        console.log("Got ID token, making request to:", `${baseUrl}/getAdminNotificationSettings`);
        
        const response = await fetch(`${baseUrl}/getAdminNotificationSettings`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        console.log("Response status:", response.status);
        const data = await response.json();
        console.log("Response data:", data);

        if (!response.ok) {
          throw new Error(data.message || "Failed to fetch notification settings");
        }

        if (data.success) {
          console.log("Setting notification settings:", data.data);
          setNotificationSettings(data.data);
        } else {
          throw new Error(data.message || "Failed to fetch notification settings");
        }
      } catch (error) {
        console.error("Error fetching notification settings:", error);
        toast.error("Failed to load notification settings", {
          description: error.message
        });
      } finally {
        console.log("Finished loading notification settings");
        setLoadingSettings(false);
      }
    };

    fetchNotificationSettings();
  }, [currentUser]); // Add currentUser as a dependency

  // Add function to handle notification settings update
  const handleNotificationSettingsUpdate = async () => {
    if (!currentUser) {
      toast.error("You must be logged in to update settings");
      return;
    }

    try {
      // Validate that there's at least one notification email when default admins are disabled
      if (!notificationSettings.useDefaultAdmins && notificationSettings.notificationEmails.length === 0) {
        toast.error("Please add at least one notification email when default admin notifications are disabled");
        return;
      }

      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${baseUrl}/updateAdminNotificationSettings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify(notificationSettings),
      });

      if (!response.ok) {
        throw new Error("Failed to update notification settings");
      }

      toast.success("Notification settings updated successfully");
    } catch (error) {
      console.error("Error updating notification settings:", error);
      toast.error("Failed to update notification settings");
    }
  };

  // Add function to handle adding new email
  const handleAddEmail = () => {
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (notificationSettings.notificationEmails.includes(newEmail)) {
      toast.error("This email is already in the list");
      return;
    }

    setNotificationSettings(prev => ({
      ...prev,
      notificationEmails: [...prev.notificationEmails, newEmail],
    }));
    setNewEmail("");
  };

  // Add function to handle removing email
  const handleRemoveEmail = (emailToRemove) => {
    setNotificationSettings(prev => ({
      ...prev,
      notificationEmails: prev.notificationEmails.filter(email => email !== emailToRemove),
    }));
  };

  // Helper to format Firestore Timestamp or Date object
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    let date;
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp?.toDate) {
      date = timestamp.toDate();
    } else {
      return 'Invalid Date';
    }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Helper to determine Badge variant based on status
  const getStatusVariant = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'destructive';
      default: return 'outline';
    }
  };

  // Handle user approval/rejection
  const handleUserStatusUpdate = async (userId, status) => {
    if (!currentUser) {
      toast.error("You must be logged in to update user status");
      return;
    }

    setProcessingUser(userId);
    try {
      const idToken = await currentUser.getIdToken();
      
      // Update the user status
      const updateResponse = await fetch(`${baseUrl}/updateUserApprovalStatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ userId, status }),
      });

      if (!updateResponse.ok) {
        const error = await updateResponse.json();
        throw new Error(error.message || 'Failed to update user status');
      }

      const result = await updateResponse.json();
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      const displayName = userData?.displayName || 'User';

      toast.success(result.message || `User ${status} successfully`, {
        description: `${displayName}'s status has been updated to ${status}`
      });
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error("Failed to update user status", {
        description: error.message || "An unexpected error occurred"
      });
    } finally {
      setProcessingUser(null);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    if (!currentUser) {
      toast.error("You must be logged in to delete users");
      return;
    }

    setProcessingUser(userId);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${baseUrl}/deleteUserAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ uid: userId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete user');
      }

      const result = await response.json();
      toast.success("User deleted successfully", {
        description: result.message || "The user account has been permanently removed"
      });
      setUserToDelete(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user", {
        description: error.message || "An unexpected error occurred"
      });
    } finally {
      setProcessingUser(null);
    }
  };

  // Handle user role update
  const handleRoleUpdate = async (userId, newRole) => {
    if (!currentUser) {
      toast.error("You must be logged in to update user roles");
      return;
    }

    setProcessingUser(userId);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${baseUrl}/updateUserRole`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ userId, role: newRole }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update user role');
      }

      const result = await response.json();
      toast.success(`User role updated to ${newRole}`, {
        description: result.message || "The user's role has been successfully updated"
      });
    } catch (error) {
      console.error("Error updating user role:", error);
      toast.error("Failed to update user role", {
        description: error.message || "An unexpected error occurred"
      });
    } finally {
      setProcessingUser(null);
    }
  };

  // Sort users: pending first, then by creation date
  const sortedUsers = [...users].sort((a, b) => {
    if (a.approvalStatus === "pending" && b.approvalStatus !== "pending") return -1;
    if (a.approvalStatus !== "pending" && b.approvalStatus === "pending") return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  // Update handleResetCache to use the new cache management
  const handleResetCache = async () => {
    if (!currentUser) {
      toast.error("You must be logged in to reset the cache");
      return;
    }

    setResettingCache(true);
    try {
      // Clear local storage
      localStorage.removeItem("rentmanData");
      localStorage.removeItem("rentmanDataTimestamp");
      localStorage.removeItem("rentmanDataVersion");
      localStorage.removeItem("rentmanDataTTL");

      // Get a fresh ID token
      const idToken = await currentUser.getIdToken(true);
      
      // Reset backend cache
      const response = await fetch(`${baseUrl}/resetRedisCache`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reset cache");
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Failed to reset cache");
      }

      toast.success("Cache has been reset successfully", {
        description: `New cache version: ${data.data.newVersion}`
      });
      
      // Reload the page to ensure fresh data
      window.location.reload();
    } catch (error) {
      console.error("Error resetting cache:", error);
      toast.error("Failed to reset cache", {
        description: error.message || "An unexpected error occurred"
      });
    } finally {
      setResettingCache(false);
    }
  };

  // Update handleUpdateTTL to also refresh the frontend cache
  const handleUpdateTTL = async () => {
    if (!currentUser) {
      toast.error("You must be logged in to update cache TTL");
      return;
    }

    setUpdatingTTL(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${baseUrl}/updateCacheTTL`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ ttlSeconds: cacheTTL * 24 * 60 * 60 }), // Convert days to seconds
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update cache TTL');
      }

      // Refresh the data after TTL update
      await refreshData();

      const result = await response.json();
      toast.success("Cache TTL updated successfully", {
        description: `Cache will now expire after ${cacheTTL} days`
      });
    } catch (error) {
      console.error("Error updating cache TTL:", error);
      toast.error("Failed to update cache TTL", {
        description: error.message || "An unexpected error occurred"
      });
    } finally {
      setUpdatingTTL(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard - User Management</h1>

      {/* Add Notification Settings Card */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Notification Settings</CardTitle>
          <CardDescription>
            Configure email notifications for new user requests.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingSettings ? (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="ml-2">Loading settings...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="useDefaultAdmins"
                  checked={notificationSettings.useDefaultAdmins}
                  onChange={(e) => setNotificationSettings(prev => ({
                    ...prev,
                    useDefaultAdmins: e.target.checked,
                  }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="useDefaultAdmins" className="text-sm font-medium">
                  Send notifications to all admin users
                </label>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {notificationSettings.useDefaultAdmins ? "Additional Notification Emails" : "Default Notification Emails"}
                  {!notificationSettings.useDefaultAdmins && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </label>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                  <Button onClick={handleAddEmail} variant="outline">
                    Add
                  </Button>
                </div>
                {notificationSettings.notificationEmails.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {notificationSettings.notificationEmails.map((email) => (
                      <div key={email} className="flex items-center justify-between bg-background p-2 rounded-md border border-border">
                        <span className="text-sm text-foreground">{email}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveEmail(email)}
                          className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                          disabled={!notificationSettings.useDefaultAdmins && notificationSettings.notificationEmails.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {!notificationSettings.useDefaultAdmins && notificationSettings.notificationEmails.length === 0 && (
                  <p className="text-sm text-destructive mt-1">
                    At least one notification email is required when default admin notifications are disabled
                  </p>
                )}
              </div>

              <Button
                onClick={handleNotificationSettingsUpdate}
                className="mt-4"
                disabled={!notificationSettings.useDefaultAdmins && notificationSettings.notificationEmails.length === 0}
              >
                Save Settings
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Cache Management Card */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Cache Management</CardTitle>
          <CardDescription>
            Manage the system cache. When you add new equipment or change the Rentman data, you can reset the cache to update the data.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Cache Duration
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={cacheTTL || 14}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value)) {
                        setCacheTTL(Math.min(90, Math.max(1, value)));
                      } else {
                        setCacheTTL(14); // Default to 14 if invalid input
                      }
                    }}
                    className="w-24 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                  <Button
                    onClick={handleUpdateTTL}
                    disabled={updatingTTL}
                    variant="outline"
                  >
                    {updatingTTL ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update TTL'
                    )}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Set how long the cache should be valid (1-90 days). After this period, the cache will automatically refresh.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                The cache is automatically refreshed every {cacheTTL} days for all users. Use this button if you added new equipment or changed equipment data or added new customers.
              </p>
              <div className="flex flex-col space-y-2">
                <Button
                  onClick={handleResetCache}
                  disabled={resettingCache}
                  variant="destructive"
                >
                  {resettingCache ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting Cache...
                    </>
                  ) : (
                    'Reset Cache and serve new Rentman data'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
          <CardDescription>
            View and manage user accounts and their approval status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex justify-center items-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="ml-2">Loading users...</p>
            </div>
          )}
          {error && <p className="text-destructive text-center py-10">{error}</p>}

          {!loading && !error && (
            <Table>
              <TableCaption>A list of all registered users.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Display Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Auth Provider</TableHead>
                  <TableHead>Signed Up</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
                {sortedUsers.map((user) => (
                  <TableRow 
                    key={user.id}
                  >
                    <TableCell className="font-medium">{user.displayName || 'N/A'}</TableCell>
                    <TableCell>{user.email || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={getStatusVariant(user.approvalStatus)} 
                        className={`capitalize ${user.approvalStatus === 'pending' ? 'text-yellow-600 dark:text-yellow-400' : ''}`}
                      >
                        {user.approvalStatus || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{user.role || 'User'}</TableCell>
                    <TableCell className="capitalize">{user.authProvider || 'N/A'}</TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" disabled={processingUser === user.id} className="relative">
                              {processingUser === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <MoreHorizontal className="h-4 w-4" />
                                  {user.approvalStatus === 'pending' && (
                                    <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-yellow-500 ring-2 ring-background" />
                                  )}
                                </>
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>User Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {user.approvalStatus === 'pending' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleUserStatusUpdate(user.id, 'approved')}
                                  disabled={processingUser === user.id}
                                >
                                  <UserX className="mr-2 h-4 w-4" />
                                  Approve User
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleUserStatusUpdate(user.id, 'rejected')}
                                  disabled={processingUser === user.id}
                                >
                                  <UserX className="mr-2 h-4 w-4" />
                                  Reject User
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            {user.approvalStatus !== 'rejected' && (
                              <DropdownMenuItem
                                onClick={() => handleRoleUpdate(user.id, user.role === 'admin' ? 'user' : 'admin')}
                                disabled={processingUser === user.id || user.approvalStatus !== 'approved'}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                {user.role === 'admin' ? 'Revoke Admin' : 'Make Admin'}
                              </DropdownMenuItem>
                            )}
                            {user.approvalStatus === 'approved' && (
                              <DropdownMenuItem
                                onClick={() => handleUserStatusUpdate(user.id, 'rejected')}
                                disabled={processingUser === user.id}
                              >
                                <UserX className="mr-2 h-4 w-4" />
                                Revoke Access
                              </DropdownMenuItem>
                            )}
                            {user.approvalStatus === 'rejected' && (
                              <DropdownMenuItem
                                onClick={() => handleUserStatusUpdate(user.id, 'approved')}
                                disabled={processingUser === user.id}
                              >
                                <UserX className="mr-2 h-4 w-4" />
                                Approve User
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setUserToDelete(user)}
                              disabled={processingUser === user.id}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user account
              {userToDelete && ` for ${userToDelete.displayName || userToDelete.email}`}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => userToDelete && handleDeleteUser(userToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default AdminPage;

// Note: For the Badge 'success' variant, you might need to add custom styles
// in your global CSS if it's not default in shadcn/ui:
/* Example global.css:
.badge-success {
  @apply border-transparent bg-green-500 text-primary-foreground shadow hover:bg-green-500/80;
}
*/